import { Injectable } from '@angular/core';

import { Storage } from '@ionic/storage-angular';
import {
  BehaviorSubject,
  from,
  map,
  Observable,
  skipWhile,
  switchMap,
  tap,
} from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private readonly _storage = new BehaviorSubject<Storage | null>(null);

  readonly storage$ = this._storage.asObservable().pipe(skipWhile((x) => !x));

  constructor(private readonly ionicStorage: Storage) {
    this.init();
  }

  init() {
    from(this.ionicStorage.create())
      .pipe(tap((storage) => this._storage.next(storage)))
      .subscribe();
  }

  set<T>(key: StorageKeys, value: T): Observable<T> {
    return this._storage.pipe(
      skipWhile((x) => !x),
      switchMap((storage) => from(storage!.set(key, value))),
      map(() => value)
    );
  }

  get<T>(key: StorageKeys): Observable<T> {
    return this._storage.pipe(
      skipWhile((x) => !x),
      switchMap((storage) => from(storage!.get(key)))
    );
  }

  remove(key: StorageKeys): Observable<void> {
    return this._storage.pipe(
      skipWhile((x) => !x),
      switchMap((storage) => from(storage!.remove(key)))
    );
  }

  getFromSessionStorage<T>(key: StorageKeys): T | null {
    const value = sessionStorage.getItem(key);
    if (typeof value !== 'string') {
      return null;
    }
    return JSON.parse(value) as T;
  }

  setToSessionStorage<T>(key: StorageKeys, value: T): void {
    return sessionStorage.setItem(key, JSON.stringify(value));
  }

  removeFromSessionStorage(key: StorageKeys): void {
    return sessionStorage.removeItem(key);
  }
}

export enum StorageKeys {
  CART = 'cart',
  AUTH_DATA = 'auth',
  LANGUAGE = 'language',
  NEW_PRODUCTS_SHOWN = 'new-products-shown',
  NO_CREDIT_CARD_ADDED_SHOWN = 'no-credit-card-added-shown',
  LAST_USED_INSTORE_LOCATION = 'LAST_USED_INSTORE_LOCATION',
}
